import React, { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";

export default function PackingVisualizer() {
  const mountRef = useRef(null);
  const [info, setInfo] = useState(null);

  useEffect(() => {
    const mount = mountRef.current;
    const width = mount.clientWidth;
    const height = mount.clientHeight;

    /** -----------------------
     *  SCENE + CAMERA
     * ------------------------*/
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xffffff);

    const camera = new THREE.PerspectiveCamera(70, width / height, 0.1, 2000);
    camera.position.set(10, 8, 12);
    camera.lookAt(0, 0, 0);

    /** RENDERER **/
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(width, height);
    mount.appendChild(renderer.domElement);

    /** DRAGGABLE CAMERA **/
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.1;
    controls.rotateSpeed = 0.6;
    controls.zoomSpeed = 0.9;

    /** LIGHT **/
    scene.add(new THREE.AmbientLight(0xffffff, 1));
    scene.add(new THREE.DirectionalLight(0xffffff, 0.3));

    /** OUTER BOX **/
    const outer = { w: 10, h: 6, d: 8 };

    const outerGeo = new THREE.BoxGeometry(outer.w, outer.h, outer.d);
    const outerMat = new THREE.MeshBasicMaterial({
      color: 0x333333,
      wireframe: true,
    });
    const outerMesh = new THREE.Mesh(outerGeo, outerMat);
    scene.add(outerMesh);

    /** -----------------------
     *  GENERATE MORE BOXES
     * ------------------------*/
    const boxes = [];
    for (let i = 0; i < 120; i++) {
      boxes.push({
        w: (Math.random() * 1.7 + 0.8).toFixed(2) * 1,
        h: (Math.random() * 1.4 + 0.6).toFixed(2) * 1,
        d: (Math.random() * 1.7 + 0.8).toFixed(2) * 1,
      });
    }

    /** -----------------------
 *  GRID STACKING PACKING
 * ------------------------*/

    const cell = 0.2; // resolusi grid, makin kecil makin akurat
    const gridX = Math.floor(outer.w / cell);
    const gridY = Math.floor(outer.h / cell);
    const gridZ = Math.floor(outer.d / cell);

    const space = Array.from({ length: gridX }, () =>
      Array.from({ length: gridY }, () =>
        Array(gridZ).fill(false)
      )
    );

    function canPlace(ix, iy, iz, bw, bh, bd) {
      const gx = Math.floor(bw / cell);
      const gy = Math.floor(bh / cell);
      const gz = Math.floor(bd / cell);

      for (let x = 0; x < gx; x++)
        for (let y = 0; y < gy; y++)
          for (let z = 0; z < gz; z++)
            if (space[ix + x]?.[iy + y]?.[iz + z] === false) continue;
            else return false;

      return true;
    }

    function occupy(ix, iy, iz, bw, bh, bd) {
      const gx = Math.floor(bw / cell);
      const gy = Math.floor(bh / cell);
      const gz = Math.floor(bd / cell);

      for (let x = 0; x < gx; x++)
        for (let y = 0; y < gy; y++)
          for (let z = 0; z < gz; z++)
            space[ix + x][iy + y][iz + z] = true;
    }

    let usedVolume = 0;
    const placedMeshes = [];

    boxes.forEach((b, index) => {
      const { w, h, d } = b;

      const gx = Math.floor(w / cell);
      const gy = Math.floor(h / cell);
      const gz = Math.floor(d / cell);

      let placed = false;

      for (let ix = 0; ix < gridX - gx; ix++) {
        if (placed) break;
        for (let iz = 0; iz < gridZ - gz; iz++) {
          if (placed) break;
          for (let iy = 0; iy < gridY - gy; iy++) {
            if (canPlace(ix, iy, iz, w, h, d)) {
              occupy(ix, iy, iz, w, h, d);

              const px = ix * cell - outer.w / 2 + w / 2;
              const py = iy * cell - outer.h / 2 + h / 2;
              const pz = iz * cell - outer.d / 2 + d / 2;

              const geo = new THREE.BoxGeometry(w, h, d);
              const mat = new THREE.MeshStandardMaterial({
                color: new THREE.Color(Math.random(), Math.random(), Math.random()),
              });

              const mesh = new THREE.Mesh(geo, mat);
              mesh.position.set(px, py, pz);
              mesh.userData = { id: index, w, h, d };

              scene.add(mesh);
              placedMeshes.push(mesh);

              usedVolume += w * h * d;

              placed = true;
              break;
            }
          }
        }
      }
    });



    /** -----------------------
     *  SIMPLE 3D SHELF PACKING
     * ------------------------*/
    // let x = -outer.w / 2;
    // let y = -outer.h / 2;
    // let z = -outer.d / 2;
    // let shelfHeight = 0;

    // const placedMeshes = [];
    // let usedVolume = 0;

    // boxes.forEach((b, index) => {
    //   const { w, h, d } = b;

    //   /** Move to next X slot */
    //   if (x + w > outer.w / 2) {
    //     x = -outer.w / 2;
    //     z += shelfHeight + 0.05;
    //     shelfHeight = 0;
    //   }

    //   /** Move to next Z layer */
    //   if (z + d > outer.d / 2) {
    //     z = -outer.d / 2;
    //     y += shelfHeight + 0.05;
    //     shelfHeight = 0;
    //   }

    //   /** Overflow Y → ignore */
    //   if (y + h > outer.h / 2) return;

    //   /** Update shelf height */
    //   shelfHeight = Math.max(shelfHeight, h);

    //   /** Create 3D mesh */
    //   const geo = new THREE.BoxGeometry(w, h, d);
    //   const mat = new THREE.MeshStandardMaterial({
    //     color: new THREE.Color(Math.random(), Math.random(), Math.random()),
    //   });
    //   const mesh = new THREE.Mesh(geo, mat);

    //   mesh.position.set(x + w / 2, y + h / 2, z + d / 2);
    //   mesh.userData = { id: index, w, h, d };

    //   scene.add(mesh);
    //   placedMeshes.push(mesh);

    //   usedVolume += w * h * d;

    //   x += w + 0.05;
    // });

    // /** -----------------------
    //  *  WASTE SPACE VISUALIZATION
    //  * ------------------------*/
    // const totalVolume = outer.w * outer.h * outer.d;
    // const wasteVolume = (totalVolume - usedVolume).toFixed(2);

    // const wasteGeo = new THREE.BoxGeometry(outer.w, outer.h, outer.d);
    // const wasteMat = new THREE.MeshStandardMaterial({
    //   color: 0xff0000,
    //   transparent: true,
    //   opacity: 0.1,
    // });
    // const wasteMesh = new THREE.Mesh(wasteGeo, wasteMat);
    // wasteMesh.position.set(0, 0, 0);
    // scene.add(wasteMesh);

    /** -----------------------
     *  CLICK RAYCAST
     * ------------------------*/
    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();

    function handleClick(e) {
      const rect = mount.getBoundingClientRect();

      mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

      raycaster.setFromCamera(mouse, camera);
      const hit = raycaster.intersectObjects(placedMeshes);

      placedMeshes.forEach(m => (m.material.emissive = new THREE.Color(0x000000)));

      if (hit.length > 0) {
        const box = hit[0].object;
        box.material.emissive = new THREE.Color(0x222222);

        setInfo({
          id: box.userData.id,
          ...box.userData,
          x: box.position.x.toFixed(2),
          y: box.position.y.toFixed(2),
          z: box.position.z.toFixed(2),
          wasteVolume,
        });
      }
    }

    mount.addEventListener("click", handleClick);

    /** -----------------------
     *  ANIMATION LOOP
     * ------------------------*/
    function animate() {
      requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    }

    animate();

    return () => {
      mount.removeChild(renderer.domElement);
      mount.removeEventListener("click", handleClick);
    };
  }, []);

  return (
    <div style={{ width: "100%", height: "650px", position: "relative" }}>
      <div ref={mountRef} style={{ width: "100%", height: "100%" }} />

      {info && (
        <div
          style={{
            position: "absolute",
            top: 10,
            right: 10,
            padding: "14px",
            background: "#000000cc",
            color: "white",
            borderRadius: "6px",
            fontSize: "14px",
            minWidth: "200px",
          }}
        >
          <strong>Box #{info.id}</strong><br />
          Size: {info.w} × {info.h} × {info.d}<br />
          Position: ({info.x}, {info.y}, {info.z})<br />
          <hr />
          <strong>Total Waste Volume:</strong><br />
          {info.wasteVolume}
        </div>
      )}
    </div>
  );
}
